import { useMemo, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import { useTranslation } from "react-i18next";
import { Namespace } from "../../locales/translations";
import { selectAllPartners } from "../../store/reducers/partner/list";
import DropdownMenu, { Option } from "../_include/DropdownMenu/DropdownMenu";
import LocalShippingIcon from "@mui/icons-material/LocalShippingRounded";
import { LocalStorageKeys } from "../../constants/local_storage";
import { useLocalStorage } from "../../hooks/local_storage";
import PartnersController from "../../actions/partner_controller";

type PartnerSelectProps = {
    parentLoading: boolean;
}

function PartnerSelect({ parentLoading }: PartnerSelectProps) {
    const dispatch = useAppDispatch();

    const loading = useAppSelector(state => state.partners.list.loading) || parentLoading;
    const partners = useAppSelector(state => selectAllPartners(state));
    const selectedPartner = useAppSelector(state => state.partners.selected.partner);
    const userPartnerPermissions = useAppSelector(state => state.user.partnerPermissions);

    const { t } = useTranslation([Namespace.COMMONS, Namespace.FORMS, Namespace.GLOSSARY, Namespace.PARTNERS,]);

    // Fetch partners by IDs for users with multiple partner permissions
    useEffect(() => {
        if (userPartnerPermissions && Object.keys(userPartnerPermissions).length > 1) {
            const partnerIDs = Object.keys(userPartnerPermissions);
            dispatch(PartnersController.getPartnersByIds(partnerIDs));
        }
    }, [userPartnerPermissions, dispatch]);

    const partnersOptions = useMemo(() => {
        let partnersOptions: Option[] = [];

        if (!selectedPartner) { // add loading option while partners are loading
            partnersOptions.push({
                label: t("loading", { ns: Namespace.COMMONS }),
                value: "",
            });
        }

        // add each partner as an option
        partners.forEach(partner => {
            // add the partner with a color indicating the status
            partnersOptions.push({
                label: partner.name,
                value: partner.ID,
            });
        });

        return partnersOptions;
    }, [selectedPartner, partners, t]);
    
    const [selectedPartnerID, setSelectedPartnerID] = useLocalStorage(LocalStorageKeys.PARTNER_ID);
    
    let defaultValue: string, title: string;
    
    const firstPartner: Option | undefined = partnersOptions[0];
    defaultValue = selectedPartnerID || firstPartner?.value;
    title = selectedPartner?.name || firstPartner?.label;

    const handlePartnerSelected = (id: string) => {
        if (id !== selectedPartnerID) setSelectedPartnerID(id);
    };

    return (
        <DropdownMenu
            id="partner-select-menu"
            title={title}
            loading={loading}
            values={partnersOptions}
            defaultValue={defaultValue}
            startIcon={<LocalShippingIcon />}
            onChange={(partnerID) => handlePartnerSelected(partnerID)}
        />
    );
}

export default PartnerSelect;